/**
 * Network Logger - Captures fetch() requests and sends to OverSkill for AI debugging
 *
 * This enables the AI to read actual network requests/responses when debugging issues.
 * Only enabled when app is viewed in OverSkill editor iframe.
 *
 * Captures:
 * - window.fetch() calls
 * - Request method, URL, status, timing
 * - Error information for failed requests
 */

import type { NetworkRequestEntry, PlatformConfig, NetworkLoggerInstance } from './types';

export class NetworkLogger implements NetworkLoggerInstance {
  private requests: NetworkRequestEntry[] = [];
  private maxRequests = 100;
  private sendInterval = 5000; // 5 seconds
  private originalFetch: typeof window.fetch | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private enabled = false;
  private isInIframe = false;

  constructor(private config: PlatformConfig) {
    // Detect iframe status early
    this.isInIframe = typeof window !== 'undefined' && window.parent !== window && window.parent !== null;

    if (this.shouldEnable()) {
      this.enabled = true;
      this.init();
    }
  }

  /**
   * Determine if network logging should be enabled
   */
  private shouldEnable(): boolean {
    if (typeof window === 'undefined') return false;

    // CRITICAL: Only enable if in iframe (being viewed in OverSkill editor)
    // Don't run when app is viewed directly (wastes requests, causes errors)
    if (!this.isInIframe) {
      this.debugLog('Not in iframe - skipping network logger');
      return false;
    }

    // Enable in development or if explicitly enabled
    const appConfig = window.APP_CONFIG || {};
    return (
      appConfig.ENVIRONMENT === 'development' ||
      this.isInIframe // Always enable if in iframe for AI debugging
    );
  }

  /**
   * Initialize fetch interception
   */
  private init(): void {
    // Store original fetch
    this.originalFetch = window.fetch.bind(window);

    // Override fetch
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const startTime = performance.now();
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const method = init?.method || 'GET';

      try {
        const response = await this.originalFetch!(input, init);
        const endTime = performance.now();

        // Don't log requests to our own bridge endpoint (would cause infinite loop)
        if (!this.isBridgeRequest(url)) {
          this.captureRequest({
            method,
            url,
            status: response.status,
            response_time: Math.round(endTime - startTime),
          });
        }

        return response;
      } catch (error) {
        const endTime = performance.now();

        // Capture failed request (unless it's to our bridge)
        if (!this.isBridgeRequest(url)) {
          this.captureRequest({
            method,
            url,
            status: 0,
            response_time: Math.round(endTime - startTime),
            error: error instanceof Error ? error.message : String(error),
          });
        }

        throw error;
      }
    };

    // Start periodic sending
    this.startPeriodicSend();

    this.debugLog('Network logging enabled via postMessage (iframe mode)');
  }

  /**
   * Check if URL is a bridge request (to avoid infinite loops)
   */
  private isBridgeRequest(url: string): boolean {
    return url.includes('/api/v1/iframe_bridge/');
  }

  /**
   * Capture a network request entry
   */
  private captureRequest(data: {
    method: string;
    url: string;
    status: number;
    response_time: number;
    error?: string;
  }): void {
    const requestEntry: NetworkRequestEntry = {
      timestamp: new Date().toISOString(),
      method: data.method.toUpperCase(),
      url: data.url,
      status: data.status,
      response_time: data.response_time,
      error: data.error,
      appId: this.config.appId,
    };

    // Add to memory buffer
    this.requests.push(requestEntry);

    // Keep only last maxRequests entries
    if (this.requests.length > this.maxRequests) {
      this.requests.shift();
    }

    // Send failed requests (status >= 400 or error) immediately
    if (data.status >= 400 || data.error) {
      this.sendRequestsToServer();
    }
  }

  /**
   * Start periodic request sending
   */
  private startPeriodicSend(): void {
    this.intervalId = setInterval(() => {
      if (this.requests.length > 0) {
        this.sendRequestsToServer();
      }
    }, this.sendInterval);
  }

  /**
   * Send requests to server via postMessage (iframe-only)
   *
   * NOTE: HTTP fallback was removed (Feb 2026) because:
   * 1. shouldEnable() already prevents initialization outside iframe
   * 2. Cross-origin HTTP to overskill.com from *.overskill.app always fails CORS
   * 3. Network logging is only useful when AI is debugging in editor iframe
   */
  private async sendRequestsToServer(): Promise<void> {
    if (this.requests.length === 0) return;

    const requestsToSend = [...this.requests];
    this.requests = []; // Clear after copying

    // Only send via postMessage when in iframe (cross-origin safe)
    if (this.isInIframe && window.parent) {
      requestsToSend.forEach((request) => {
        try {
          window.parent.postMessage(
            {
              type: 'network_request',
              request: {
                method: request.method,
                url: request.url,
                status: request.status,
                response_time: request.response_time,
                error: request.error,
                timestamp: request.timestamp,
                appId: request.appId,
              },
            },
            '*'
          );
        } catch {
          // Silent fail - cross-origin postMessage should rarely fail
        }
      });
      this.debugLog(`Sent ${requestsToSend.length} network requests via postMessage`);
      return;
    }

    // Not in iframe - discard requests silently
    // This should never happen since shouldEnable() returns false outside iframe,
    // but guard defensively to prevent CORS errors
    this.debugLog('Not in iframe - discarding network requests (no safe transport available)');
  }

  /**
   * Debug log helper (only when debug mode enabled)
   */
  private debugLog(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.log(`[OverSkill Platform SDK] ${message}`, data || '');
    }
  }

  // Public API

  /**
   * Get captured requests
   */
  getRequests(): NetworkRequestEntry[] {
    return [...this.requests];
  }

  /**
   * Clear captured requests
   */
  clearRequests(): void {
    this.requests = [];
  }

  /**
   * Manually flush requests to server
   */
  async flush(): Promise<void> {
    await this.sendRequestsToServer();
  }

  /**
   * Check if logger is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Cleanup (for testing)
   */
  destroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    // Restore original fetch
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
    }
  }
}
